import { useIntl } from 'react-intl';

import { formatCurrency, formatDate } from '../../config';
import { AEG_NEXT_STAGES, formatAegAssignTo, formatAegEnum, type Request } from '../../types';
import './styles.css';

type CardProps = {
    request: Request;
    onSelect: (id: string) => void;
    onDragStart: (id: string) => void;
};

const present = (value?: string | null) => {
    const text = value?.trim();
    return text && text !== '—' && text !== '-' ? text : '';
};

const dash = (value?: string | null) => present(value) || '-';

export default function Card({ request, onSelect, onDragStart }: CardProps) {
    const intl = useIntl();
    const label = (id: string) => intl.formatMessage({ id: `intl-msg:${id}` });
    const canMove = AEG_NEXT_STAGES[request.stage].length > 0;
    const client = dash(request.client);
    const model = present(request.model);
    const plate = present(request.plate);
    const vehicle = [model, plate].filter(Boolean).join(' · ') || '-';
    const approval = dash(formatAegEnum(intl, 'approvalType', request.approval));
    const owner = dash(request.owner);
    const assignedTo = dash(formatAegAssignTo(intl, request.assignedTo));
    const dealer = dash(request.dealer);

    return (
        <article
            className='aeg-card-root panel panel-default padding-10 margin-top-10 cursor-pointer text-color-darkest bg-white'
            draggable={canMove}
            onClick={() => onSelect(request.id)}
            onDragStart={event => {
                event.dataTransfer.setData('requestId', request.id);
                event.dataTransfer.effectAllowed = 'move';
                onDragStart(request.id);
            }}
        >
            <div className='display-flex align-items-center justify-content-between gap-10'>
                <span
                    className={`aeg-card-number aeg-card-mono ${request.aegNumber ? '' : 'text-italic text-color-dark'}`}
                >
                    {request.aegNumber || label('support.aeg.card.noNumber')}
                </span>
                <small className='aeg-card-date text-color-dark'>{dash(formatDate(request.date))}</small>
            </div>
            <div>
                <b className='aeg-card-line display-block text-color-darkest' title={client}>
                    {client}
                </b>
                <small className='aeg-card-line aeg-card-meta display-block text-color-dark' title={vehicle}>
                    {model && plate ? (
                        <>
                            {model} · <span className='aeg-card-mono'>{plate}</span>
                        </>
                    ) : (
                        <span className={plate ? 'aeg-card-mono' : undefined}>{vehicle}</span>
                    )}
                </small>
            </div>
            <dl className='aeg-card-details margin-0 text-size-12'>
                <dt className='text-color-dark'>{label('support.aeg.card.repair')}</dt>
                <dd className='margin-0 text-right text-bold'>{formatCurrency(request.value)}</dd>
                <dt className='text-color-dark'>{label('support.aeg.card.approval')}</dt>
                <dd className='margin-0 text-right' title={approval}>
                    {approval}
                </dd>
                <dt className='text-color-dark'>{label('support.aeg.card.responsible')}</dt>
                <dd className='margin-0 text-right' title={owner}>
                    {owner}
                </dd>
                <dt className='text-color-dark'>{label('support.aeg.card.assignedTo')}</dt>
                <dd className='margin-0 text-right' title={assignedTo}>
                    {assignedTo}
                </dd>
                <dt className='text-color-dark'>{label('support.aeg.card.dn')}</dt>
                <dd className='margin-0 text-right' title={dealer}>
                    {dealer}
                </dd>
            </dl>
        </article>
    );
}
